// One-shot hydration gate: DB init + seed + all stores loaded before UI shows.

import { useEffect, useState } from 'react';

import { initAnalytics } from '@/analytics/analytics';
import { getDb } from '@/db';
import { initCloudSync, syncNow } from '@/sync/cloud-sync';
import { initHouseholdSync, syncHouseholdNow } from '@/sync/household-sync';
import { useAppMetaStore } from '@/store/app-meta-store';
import { useAuthStore } from '@/store/auth-store';
import { useBudgetStore } from '@/store/budget-store';
import { useGoalsStore } from '@/store/goals-store';
import { useHouseholdStore } from '@/store/household-store';
import { useIdeasStore } from '@/store/ideas-store';
import { usePoliciesStore } from '@/store/policies-store';
import { useFixedCostsStore } from '@/store/fixed-costs-store';
import { useIncomeStore } from '@/store/income-store';
import { useForecastHistoryStore } from '@/store/forecast-history-store';
import { useRemindersStore } from '@/store/reminders-store';
import { useVaultStore } from '@/store/vault-store';
import { recordForecastSnapshotIfNeeded } from '@/utils/forecast-snapshot';

export function useAppHydration(): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await getDb();
        await Promise.all([
          useAppMetaStore.getState().hydrate(),
          useAuthStore.getState().hydrate(),
          useBudgetStore.getState().hydrate(),
          useGoalsStore.getState().hydrate(),
          useHouseholdStore.getState().hydrate(),
          useIdeasStore.getState().hydrate(),
          usePoliciesStore.getState().hydrate(),
          useRemindersStore.getState().hydrate(),
          useVaultStore.getState().hydrate(),
          useIncomeStore.getState().hydrate(),
          useFixedCostsStore.getState().hydrate(),
          useForecastHistoryStore.getState().hydrate(),
        ]);
        // Once per calendar day: log what the app predicts today (the honest
        // history behind the summary calendar). Needs hydrated stores — hence
        // right after the Promise.all above.
        await recordForecastSnapshotIfNeeded().catch(() => undefined);
        // Analytics is opt-in and a no-op until the user consents — safe to load
        // here; it only wakes PostHog if consent was previously granted.
        await initAnalytics();
        // Cloud sync is opt-in too; syncNow() no-ops unless enabled + logged in.
        await initCloudSync();
        void syncNow();
        // Shared household sync (realny sync gospodarstwa); both no-op unless
        // logged in AND a member of a server household (feature-detected).
        await initHouseholdSync();
        void syncHouseholdNow();
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return ready;
}
